import ProgressBar from "components/ProgressBar";
import {
  createContext,
  useState,
  Children,
  isValidElement,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import styled from "styled-components";
import autoAnimate from "@formkit/auto-animate";
import { getExperiment } from "utils/ABTesting";
import analytics, { omitSensitiveData } from "utils/analytics";
import Button from "./components/Button";

const Context = createContext({});

export const useStepper = () => useContext(Context);

/**
 * Stepper is a simple form carousel that collects
 * data from each Step
 */
const Stepper = ({
  children,
  debug,
  startFrom,
  initialData,
  $progressBar,
  $progressBarFrom,
}) => {
  Children.forEach(children, (child, index) => {
    if (!child) {
      return;
    }

    if (!isValidElement(child)) {
      throw new Error(`The step ${index + 1} is not a valid Element`);
    }
    if (!child.props.id) {
      throw new Error(`The step ${index + 1} is missing its 'id'`);
    }
  });

  const [state, setState] = useState({
    index: process.env.NODE_ENV === "production" ? 0 : startFrom || 0,
    data: initialData || {},
  });

  const getDefault = (key) =>
    key.split(".").reduce((value, k) => value?.[k], state.data) || null;

  const childrenArr = useMemo(
    () => Children.toArray(children).filter(Boolean),
    [children]
  );

  const getCurrentStepName = useCallback(
    () => childrenArr[state.index]?.props.id,
    [childrenArr, state.index]
  );

  const findNewIndex = (move, index) => {
    const newIndex = move === "backwards" ? index - 1 : index + 1;

    if (
      childrenArr[newIndex]?.props[
        move === "backwards" ? "omitBack" : "omitNext"
      ]
    ) {
      return findNewIndex(move, newIndex);
    } else {
      return newIndex;
    }
  };

  const setData = (stepName, data, merge) => {
    setState({
      ...state,
      data: merge
        ? {
            ...state.data,
            [stepName]: {
              ...state.data[stepName],
              ...data,
            },
          }
        : {
            ...state.data,
            [stepName]: data,
          },
    });
  };

  const back = (data) => {
    if (state.index > 0) {
      const stepName = getCurrentStepName();

      setState({
        ...state,
        index: findNewIndex("backwards", state.index),
        data: data
          ? {
              ...state.data,
              [stepName]: data,
            }
          : state.data,
      });

      setTimeout(() => {
        window.scroll({ top: 0 });
      });
    }
  };

  const next = (data) => {
    if (state.index < children.length) {
      const stepName = getCurrentStepName();

      analytics.sendEvent("step_next", {
        step: stepName,
        selectedData: omitSensitiveData(data || {}),
      });

      setState((currentState) => ({
        ...currentState,
        index: findNewIndex("forward", currentState.index),
        data: data
          ? {
              ...currentState.data,
              [stepName]: data,
            }
          : currentState.data,
      }));

      setTimeout(() => {
        window.scroll({ top: 0 });
      });
    }
  };

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      setState((state) => ({
        ...state,
        index: startFrom,
      }));
    }
  }, [startFrom]);

  useEffect(() => {
    const stepName = getCurrentStepName();

    analytics.sendEvent("step_view", {
      step: stepName,
      initialData: omitSensitiveData(state.data[stepName] || {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.index]);

  const progress = useCallback(() => {
    const times = childrenArr
      .map((child) =>
        child.props.timeToComplete != null ? child.props.timeToComplete : 10
      )
      .slice($progressBarFrom);
    const totalTime = times.reduce((sum, n) => sum + n, 0);

    const consumedTime = times
      .filter((_t, i) => i >= $progressBarFrom && i < state.index)
      .reduce((sum, n) => sum + n, 0);

    return ((consumedTime * 100) / totalTime).toFixed(0);
  }, [childrenArr, state, $progressBarFrom]);

  const parent = useRef(null);

  useEffect(() => {
    parent.current && autoAnimate(parent.current);
  }, [parent]);

  return (
    <>
      {$progressBar && (
        <div
          style={{
            marginBottom: 20,
            marginLeft: "calc(50% - 50vw)",
            marginRight: "calc(50% - 50vw)",
          }}
        >
          <ProgressBar value={progress()} />
        </div>
      )}

      <div
        ref={parent}
        style={{
          maxWidth: "600px",
          margin: "auto",
          padding: 15,
        }}
      >
        <Context.Provider
          value={{
            state,
            back,
            next,
            getDefault,
            setData,
          }}
        >
          {childrenArr[state.index]}
        </Context.Provider>

        {debug && (
          <div style={{ paddingTop: 30, marginBottom: 1000 }}>
            <div>
              <strong style={{ textTransform: "uppercase" }}>Debugger</strong>
            </div>
            <pre>
              <code>
                {JSON.stringify(
                  {
                    experiment: getExperiment(),
                    ...state,
                  },
                  null,
                  2
                )}
              </code>
            </pre>
          </div>
        )}
      </div>
    </>
  );
};

Stepper.defaultProps = {
  startFrom: 0,
  $progressBarFrom: 0,
};

export default Stepper;

export const Step = ({ children }) => {
  return <div>{children}</div>;
};

const NavList = styled.ul`
  /* display: ${({ $layout }) => ($layout === "stack" ? "block" : "grid")}; */
  /* grid-template-columns: repeat(2, 1fr); */
  display: grid;
  grid-template-columns: ${({ $layout }) =>
    $layout === "stack" ? "repeat(1, 1fr)" : "repeat(2, 1fr)"};
  gap: 15px;
  padding: 30px 0;
`;

export const Navigator = ({
  saveDataHandler,
  $type,
  $disableContinue,
  $hideContinue,
  $continueText,
  $layout,
}) => {
  const { state, back, next } = useStepper();
  const canGoBack = state.index > 0;

  if (!canGoBack) {
    return (
      <div
        className="text-center"
        style={{
          padding: "30px 0",
        }}
      >
        <Button
          disabled={$disableContinue}
          type={$type === "form" ? "submit" : "button"}
          onClick={() => {
            if ($type !== "form") {
              next(saveDataHandler?.());
            }
          }}
        >
          {$continueText}
        </Button>
      </div>
    );
  }

  if ($hideContinue) {
    return (
      <div
        className="text-center"
        style={{
          padding: "30px 0",
        }}
      >
        <Button
          disabled={$disableContinue}
          $type="secondary"
          style={{ margin: "0 auto" }}
          onClick={() => {
            back(saveDataHandler?.());
          }}
        >
          Regresar
        </Button>
      </div>
    );
  }

  return (
    <NavList $layout={$layout}>
      {$layout !== "stack" && (
        <li>
          <Button
            $fluid
            $type="secondary"
            onClick={() => {
              back(saveDataHandler?.());
            }}
          >
            Regresar
          </Button>
        </li>
      )}
      {!$hideContinue && (
        <li>
          <Button
            $fluid
            disabled={$disableContinue}
            type={$type === "form" ? "submit" : "button"}
            onClick={() => {
              if ($type !== "form") {
                next(saveDataHandler?.());
              }
            }}
          >
            {$continueText}
          </Button>
        </li>
      )}
      {$layout === "stack" && (
        <li>
          <Button
            $fluid
            $type="tertiary"
            onClick={() => {
              back(saveDataHandler?.());
            }}
          >
            Regresar
          </Button>
        </li>
      )}
    </NavList>
  );
};

Navigator.defaultProps = {
  $continueText: "Continuar",
};
